import { memo, useEffect, useMemo, useRef, useState } from "react"
import QuickWidgetDialog from "../../../quickWidgetDialog/quickWidgetDialog";
import { QuickWidgetTypes } from "../../../../reducer/user.reducer";
import { useSelector } from "react-redux";
import { selectPrimarySecurity } from "../../../../selectors/home";
import { selectOverviewSecurity } from "../../../../selectors/security";
import { selectMarketDepthSecurity } from "../../../../selectors/common";
import MarketDepth from "../../../marketDepth/marketDepth";
import { useDispatch } from "react-redux";
import { getOrderBook } from "../../../../services/apis/candles";
import { updateOrderBooks } from "../../../../reducer/security.reducer";
import { updateMarketDepthSecurity } from "../../../../reducer/common.reducer";
import { Box, CircularProgress, Typography, useTheme } from '@mui/material'
import websocketActions from "../../../../actions/websocket.actions";
import OrderBookOverview from "../../../orderBookOverview/orderBookOverview";
import SymbolDisplay from "../../../symbolDisplay/symbolDisplay";
import { calculateSnapshotDetails, getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { Security, SecurityCategory_CategoryCode } from "../../../../proto/generated/security";
import { isTradeableSecurity } from "../../../../util/cashInstrument";
import { useLocation } from "react-router-dom";
import { useSnapshotTick } from "../../../../hooks/useSecurityMarketTicks";
import { toLocaleStringIn } from "../../../../util/general";
import { green, red } from "../../../../assets/colors";

const MarketDepthWidgetLtp = memo(function MarketDepthWidgetLtp({ security }: { security: Security }) {
    const securityKey = useMemo(
        () => getSecurityKey(security.exchange, security.code),
        [security.exchange, security.code]
    )
    const snapshot = useSnapshotTick(securityKey)
    const stats = useMemo(
        () => (snapshot ? calculateSnapshotDetails(security, snapshot) : null),
        [security, snapshot]
    )

    if (!stats || !snapshot?.previousClose || stats.ltp <= 0) return null

    const changeColor = stats.ltpChange > 0 ? green : stats.ltpChange < 0 ? red : 'inherit'
    const changeText = `${stats.ltpChange > 0 ? '+' : ''}${stats.ltpChange.toFixed(2)} (${stats.changePercentage > 0 ? '+' : ''}${stats.changePercentage.toFixed(2)}%)`

    return (
        <Box
            display="flex"
            alignItems="baseline"
            className="font-feature-num"
            sx={{ flexShrink: 0, ml: 1, gap: 0.5 }}
        >
            <Typography component="span" fontSize={12} lineHeight={1.2}>
                {toLocaleStringIn(stats.ltp)}
            </Typography>
            <Typography component="span" fontSize={10} lineHeight={1.2} color="text.secondary">
                INR
            </Typography>
            <Typography component="span" fontSize={11} lineHeight={1.2} sx={{ color: changeColor, ml: 0.5 }}>
                {changeText}
            </Typography>
        </Box>
    )
})

const MarketDepthWidget: React.FC = () => {

    const theme = useTheme()
    const dispatch = useDispatch();
    const location = useLocation();
    const { primary: security, expressionMode, expressionSecurities, key } = useSelector(selectPrimarySecurity)
    const marketDepthSecurity = useSelector(selectMarketDepthSecurity)
    const overviewSecurityKey = useSelector(selectOverviewSecurity)
    
    const isScreenerPage = location.pathname.includes('/screener')
    
    const [displaySecurity, setDisplaySecurity] = useState<Security | undefined>(undefined)
    const [fallbackSecurity, setFallbackSecurity] = useState<Security | undefined>(undefined)
    const niftyFallbackRun = useRef(false)

    const securityToUse = useMemo(() => {
        if (isScreenerPage && overviewSecurityKey) {
            return overviewSecurityKey
        } else if (marketDepthSecurity) {
            return marketDepthSecurity
        } else if (security) {
            return security
        } else {
            return fallbackSecurity
        }
    }, [isScreenerPage, overviewSecurityKey, marketDepthSecurity, security, fallbackSecurity])

    useEffect(() => {
        if (security && !isScreenerPage) {
            const primaryKey = getSecurityKey(security.exchange, security.code)
            const currentMarketDepthKey = marketDepthSecurity ? getSecurityKey(marketDepthSecurity.exchange, marketDepthSecurity.code) : null
            
            if (primaryKey !== currentMarketDepthKey) {
                dispatch(updateMarketDepthSecurity(security))
            }
        }
    }, [security, marketDepthSecurity, dispatch, isScreenerPage])

    useEffect(() => {
        if (!security && !marketDepthSecurity && !isScreenerPage && !fallbackSecurity && !niftyFallbackRun.current) {
            niftyFallbackRun.current = true
            const timer = setTimeout(() => {
                getSecurityAsync('NSE:NIFTY_50').then(sec => {
                    if (sec) {
                        setFallbackSecurity(sec)
                    }
                })
            }, 50)
            return () => clearTimeout(timer)
        }
    }, [security, marketDepthSecurity, isScreenerPage, fallbackSecurity])

    useEffect(() => {
        if ((security || marketDepthSecurity) && fallbackSecurity) {
            setFallbackSecurity(undefined)
            niftyFallbackRun.current = false
        }
    }, [security, marketDepthSecurity, fallbackSecurity])

    useEffect(() => {
        if (securityToUse) {
            if (typeof securityToUse === 'string') {
                getSecurityAsync(securityToUse).then(sec => {
                    setDisplaySecurity(sec)
                })
            } else {
                setDisplaySecurity(securityToUse)
            }
        } else {
            setDisplaySecurity(undefined)
        }
    }, [securityToUse])


    useEffect(() => {
        if (displaySecurity) {
            getOrderBook({
                security: displaySecurity,
                dispatch: dispatch
            })
                .then((result) => {
                    const orderBook = { ...result }
                    if (!orderBook.security) {
                        orderBook.security = { ...displaySecurity }
                    }
                    dispatch(updateOrderBooks([orderBook]))
                })
                .catch((error) => {
                    console.error('Error fetching order book:', error)
                })

            const securityRef = {
                exchange: displaySecurity.exchange,
                code: displaySecurity.code
            }
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [securityRef],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [securityRef]
                    }
                ]
            })
        }
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: [],
                        topBidAskOnly: false
                    }
                ],
                securitySnapshotSubscriptions: [
                    {
                        tag: 'marketDepthWidget',
                        securities: []
                    }
                ]
            })
        }
    }, [displaySecurity, dispatch])

    const title = useMemo(() => {
        return (
            <Box display="flex" alignItems="center" minWidth={0} width="100%" pr={1}>
                <Box minWidth={0} overflow="hidden">
                    <SymbolDisplay
                        securityList={expressionMode ? expressionSecurities : (displaySecurity ? [displaySecurity] : [])}
                        expression={key || ''}
                        expressionMode={expressionMode}
                        expressionName={''}
                        fontSize={12}
                        logoSize={20}
                        maxWidth="auto"
                        displayEvents={false}
                    />
                </Box>
                {!expressionMode && displaySecurity && (
                    <MarketDepthWidgetLtp security={displaySecurity} />
                )}
            </Box>
        )
    }, [expressionMode, key, expressionSecurities, displaySecurity])


    return <QuickWidgetDialog widgetType={QuickWidgetTypes.MARKET_DEPTH} title={title} >

        {expressionMode ?
            <Box padding={4} textAlign="center">Depth not available for expressions.</Box> : (
                displaySecurity ? (
                    <>
                        {displaySecurity.categoryCode !== SecurityCategory_CategoryCode.INDEX && (
                            <Box padding={theme.spacing(1, 1, 0.5)}>
                                <MarketDepth security={displaySecurity} tradeable={isTradeableSecurity(displaySecurity)} />
                            </Box>
                        )}
                        <Box padding={theme.spacing(1)}>
                            <OrderBookOverview security={displaySecurity} />
                        </Box>
                    </>
                ) : <Box padding={4} textAlign="center"><CircularProgress size={20} /></Box>
            )
        }

    </QuickWidgetDialog>
}

export default memo(MarketDepthWidget);
